interface ShortNameProps {
  children?: string | null
}

const GREEK_TOKEN_MAP: Record<string, string> = {
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  omicron: 'ο',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',
  Alpha: 'Α',
  Beta: 'Β',
  Gamma: 'Γ',
  Delta: 'Δ',
  Epsilon: 'Ε',
  Zeta: 'Ζ',
  Eta: 'Η',
  Theta: 'Θ',
  Iota: 'Ι',
  Kappa: 'Κ',
  Lambda: 'Λ',
  Mu: 'Μ',
  Nu: 'Ν',
  Xi: 'Ξ',
  Omicron: 'Ο',
  Pi: 'Π',
  Rho: 'Ρ',
  Sigma: 'Σ',
  Tau: 'Τ',
  Upsilon: 'Υ',
  Phi: 'Φ',
  Chi: 'Χ',
  Psi: 'Ψ',
  Omega: 'Ω',
}

const greekTokenPattern = new RegExp(
  `\\\\?(${Object.keys(GREEK_TOKEN_MAP)
    .sort((a, b) => b.length - a.length)
    .join('|')})(?=\\b)`,
  'g',
)

function normalizeGreekTokens(input: string): string {
  return input.replace(greekTokenPattern, (_match, token: string) => GREEK_TOKEN_MAP[token] ?? token)
}

function isScriptBoundaryChar(char: string): boolean {
  return char === '_' || char === '^' || /\s/.test(char)
}

export function ShortName({ children }: ShortNameProps) {
  if (!children) return null

  const normalized = normalizeGreekTokens(children)
  const parts: React.ReactNode[] = []
  let index = 0
  let key = 0

  while (index < normalized.length) {
    const current = normalized[index]
    if (current === '_' || current === '^') {
      const isSub = current === '_'
      index++

      let content = ''
      if (index < normalized.length && normalized[index] === '{') {
        index++
        while (index < normalized.length && normalized[index] !== '}') {
          content += normalized[index]
          index++
        }
        if (index < normalized.length && normalized[index] === '}') {
          index++
        }
      } else if (index < normalized.length) {
        while (index < normalized.length && !isScriptBoundaryChar(normalized[index])) {
          content += normalized[index]
          index++
        }

        if (!content && index < normalized.length) {
          content = normalized[index]
          index++
        }
      }

      if (content) {
        parts.push(
          isSub ? (
            <sub key={key++} className="align-sub">
              {content}
            </sub>
          ) : (
            <sup key={key++} className="align-super">
              {content}
            </sup>
          ),
        )
      } else {
        parts.push(<span key={key++}>{current}</span>)
      }
      continue
    }

    let text = ''
    while (index < normalized.length && normalized[index] !== '_' && normalized[index] !== '^') {
      text += normalized[index]
      index++
    }
    if (text) {
      parts.push(<span key={key++}>{text}</span>)
    }
  }

  return <span>{parts}</span>
}
